/**
 * Fait : une consigne dans la bibliothèque — son titre, sa description, le
 *   texte qu'on ouvre et qu'on referme, et les trois gestes qu'on peut faire
 *   dessus : le COPIER, la modifier, la supprimer.
 *
 *   COPIER EST OFFERT À TOUT LE MONDE, modifier et supprimer seulement à son
 *   auteur. Ce n'est pas une garde — cacher un bouton ne protège rien, la
 *   route refuse de son côté (critère 149) — c'est une politesse : proposer un
 *   geste qui sera refusé fait perdre un clic et une explication.
 *
 *   LE TEXTE VIENT D'UN INCONNU — un autre membre de la famille l'a écrit. Il
 *   s'affiche en TEXTE, jamais en balise : c'est la règle de sécurité que le
 *   plan pose pour les lots 5 et 6, et React la tient ici tant qu'on ne lui
 *   demande pas d'interpréter du HTML.
 *
 * Ne fait pas : il ne charge rien lui-même et ne décide pas de l'ordre de la
 *   liste. La suppression passe par lui, mais c'est l'écran qui rafraîchit —
 *   une ligne qui se retirerait toute seule mentirait si le serveur refusait.
 */

import { useState } from 'react';

import { Annonce } from '../gabarits/Annonce.tsx';
import { FenetreDeConfirmation } from '../gabarits/FenetreDeConfirmation.tsx';
import type { ConsigneVue } from './api-consignes.ts';
import { copieDansLePressePapier } from './copie-dans-le-presse-papier.ts';

/** Ce que la copie a donné, tel que l'écran le dit. */
type EtatDeCopie = null | 'copie' | 'refuse';

export function ConsigneDeLaBibliotheque({
  consigne,
  surModification,
  surSuppression,
}: {
  readonly consigne: ConsigneVue;
  readonly surModification: () => void;
  readonly surSuppression: () => void;
}) {
  const [ouverte, setOuverte] = useState(false);
  const [copie, setCopie] = useState<EtatDeCopie>(null);

  return (
    <li className="carte" data-role="consigne" data-consigne={consigne.id}>
      <h3 data-role="titre-de-consigne">{consigne.titre}</h3>

      {consigne.description !== '' && (
        <p className="texte-attenue" data-role="description-de-consigne">
          {consigne.description}
        </p>
      )}

      <p className="pour-lecteur-decran" data-role="auteur-de-consigne">
        {consigne.aMoi ? 'Vous avez écrit cette consigne.' : 'Écrite par un autre membre.'}
      </p>

      <div className="barre-de-boutons">
        <button
          type="button"
          data-role="ouvrir-la-consigne"
          aria-expanded={ouverte}
          onClick={() => setOuverte(!ouverte)}
        >
          {ouverte ? 'Masquer le texte' : 'Lire le texte'}
        </button>

        <button
          type="button"
          data-role="copier-la-consigne"
          onClick={() => {
            void copieDansLePressePapier(consigne.texte).then((issue) => setCopie(issue));
          }}
        >
          Copier le texte
        </button>

        {consigne.aMoi && (
          <button type="button" data-role="modifier-la-consigne" onClick={surModification}>
            Modifier
          </button>
        )}

        {consigne.aMoi && (
          <FenetreDeConfirmation
            libelle="Supprimer"
            titre={`Supprimer « ${consigne.titre} » ?`}
            libelleDeConfirmation="Supprimer cette consigne"
            data-role="supprimer-la-consigne"
            surConfirmation={surSuppression}
          >
            <p>
              Cette consigne disparaîtra de la bibliothèque pour toute la famille, et rien ne la
              rattrapera. Les textes déjà envoyés dans des conversations, eux, restent où ils sont.
            </p>
          </FenetreDeConfirmation>
        )}
      </div>

      {/*
       * L'ANNONCE DE LA COPIE. Une copie ne change rien à l'écran : sans cette
       * phrase, un bouton qui a marché est indiscernable d'un bouton mort —
       * et pour quelqu'un qui voit mal, c'est le seul retour possible
       * (critère 150).
       */}
      {copie === 'copie' && <Annonce data-role="copie-reussie">Le texte est copié.</Annonce>}

      {copie === 'refuse' && (
        <Annonce urgence="urgent" nature="refus" data-role="copie-refusee">
          Votre navigateur a refusé la copie. Ouvrez le texte, il se sélectionne à la main.
        </Annonce>
      )}

      {ouverte && (
        <pre className="texte-de-consigne" data-role="texte-de-consigne">
          {consigne.texte}
        </pre>
      )}
    </li>
  );
}
